import { useEffect, useState } from "react";
import { deviceApi, type DeviceInvoice } from "../../infrastructure/deviceApi";

const POLL_INTERVAL_MS = 2_000;

/**
 * Espera la confirmación del cobro consultando la invoice.
 *
 * En v1 esto escuchaba el WebSocket `/ws/payments` y sólo caía a polling como respaldo.
 * Con el rail v2 el WebSocket NO sirve: lo alimenta únicamente `confirm_payment` de v1, y
 * además emite a todos los clientes conectados sin scope de tenant — reusarlo habría
 * filtrado el `payment_hash` de un comercio a otro.
 *
 * El polling no es un downgrade: `GET /invoices/{id}` **reconcilia contra el proveedor**
 * antes de responder, así que también confirma cobros que ningún webhook avisó. Es el mismo
 * camino que usa la app móvil.
 */
export function usePaymentStatus(invoiceId: string | null) {
  const [invoice, setInvoice] = useState<DeviceInvoice | null>(null);

  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;

    const check = async () => {
      try {
        const data = await deviceApi.invoices.get(invoiceId);
        if (cancelled) return;
        setInvoice(data);
        if (data.status !== "pending") clearInterval(timer);
      } catch {
        // reintentar en el siguiente tick: una consulta fallida no significa impago
      }
    };

    const timer = setInterval(check, POLL_INTERVAL_MS);
    check();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [invoiceId]);

  return {
    invoice,
    confirmed: invoice?.status === "paid",
    expired: invoice?.status === "expired" || invoice?.status === "cancelled",
  };
}
